import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Oval } from 'react-loader-spinner';
import { ImageModel } from '../types';
import ImagePreview from './ImagePreview'; // 导入图片预览组件

interface CanvasProps {
  prompt: string;
  selectedModel: ImageModel | null;
  isGenerating: boolean;
  aspectRatio: string;
  previewImages: string[] | null;
  count: number;
}

const Canvas: React.FC<CanvasProps> = ({
  prompt,
  selectedModel,
  isGenerating,
  aspectRatio,
  previewImages,
  count
}) => {
  // 图片预览状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageIndex, setPreviewImageIndex] = useState(0);

  // 打开图片预览
  const openPreview = (imageUrl: string, index: number = 0) => {
    setPreviewImageUrl(imageUrl);
    setPreviewImageIndex(index);
    setPreviewOpen(true);
  };

  // 关闭图片预览
  const closePreview = () => {
    setPreviewOpen(false);
  };

  // 导航到指定索引的图片
  const navigateToImage = (index: number) => {
    setPreviewImageIndex(index);
  };

  // 根据生成数量获取网格布局样式 - 严格遵循宽高比
  const getGridStyle = () => {
    switch (count) {
      case 2:
        return 'grid-cols-2 gap-3';
      case 3:
        // 对于3张图片，严格保持等宽布局
        return 'grid-cols-3 gap-3';
      case 4:
        return 'grid-cols-2 grid-rows-2 gap-3';
      default:
        return '';
    }
  };

  // 根据生成数量获取图片容器样式 - 严格遵循宽高比
  const getImageContainerStyle = (index: number) => {
    // 根据宽高比设置容器样式
    let aspectStyle = '';
    
    switch (aspectRatio) {
      case '16:9':
        aspectStyle = 'aspect-[16/9]';
        break;
      case '4:3':
        aspectStyle = 'aspect-[4/3]';
        break;
      case '3:4':
        aspectStyle = 'aspect-[3/4]';
        break;
      case '9:16':
        aspectStyle = 'aspect-[9/16]';
        break;
      case '1:1':
      default:
        aspectStyle = 'aspect-square';
        break;
    }
    
    return `w-full h-full ${aspectStyle}`;
  };

  // 获取预览容器样式
  const getPreviewContainerStyle = () => {
    // 对于所有宽高比，使用固定尺寸限制尺寸
    let width: number;
    let height: number;
    
    // 采用新的尺寸策略，确保所有宽高比下都有足够的宽度
    const maxHeight = 750; // 限制最大高度
    const minWidth = 1000;  // 确保最小宽度，增加到1000px
    
    switch (aspectRatio) {
      case '16:9':
        // 横向宽屏比例
        height = maxHeight;
        width = Math.max(minWidth, (16/9) * height);
        break;
      case '4:3':
        // 横向标准比例
        height = maxHeight;
        width = Math.max(minWidth, (4/3) * height);
        break;
      case '3:4':
        // 纵向标准比例 - 增大宽度以提升浏览体验
        height = maxHeight;
        // 对于纵向比例，强制使用最小宽度
        width = minWidth;
        break;
      case '9:16':
        // 纵向窄长比例 - 增大宽度以提升浏览体验
        height = maxHeight;
        // 对于纵向比例，强制使用最小宽度
        width = minWidth;
        break;
      case '1:1':
      default:
        // 正方形比例
        height = maxHeight;
        width = Math.max(minWidth, height);
        break;
    }
    
    return {
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: '100%',
      margin: '0 auto'
    };
  };

  return (
    <div className="flex-1 p-0 pt-3 flex flex-col items-center">
      <div className="w-full max-w-5xl mt-0">
        {/* 预览容器 - 根据宽高比使用不同的样式 */}
        <div 
          className="relative rounded-xl overflow-hidden shadow-art bg-dark-700 border border-dark-600 w-full" 
          style={getPreviewContainerStyle()}
        >
          {/* 背景网格 */}
          <div className="absolute inset-0 bg-dark-800 opacity-50" style={{
            backgroundImage: 'linear-gradient(#374151 1px, transparent 1px), linear-gradient(90deg, #374151 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
          
          {/* 加载动画 */}
          {isGenerating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-dark-800 bg-opacity-70 backdrop-blur-sm">
              <Oval
                height={80}
                width={80}
                color="#0ea5e9"
                secondaryColor="#d946ef"
                strokeWidth={2}
                strokeWidthSecondary={2}
              />
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-4 text-white font-medium"
              >
                AI正在绘制您的创意...
              </motion.p>
            </div>
          )}

          {/* 预览图片 - 支持多张图片显示 */}
          {previewImages && previewImages.length > 0 && !isGenerating && (
            <div className="absolute inset-0 flex items-center justify-center">
              {count === 1 ? (
                // 单张图片显示
                <img
                  src={previewImages[0]}
                  alt={prompt}
                  className="max-w-full max-h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => openPreview(previewImages[0], 0)}
                  onLoad={() => console.log('图片加载成功:', previewImages[0])}
                  onError={(e) => {
                    console.error('图片加载失败:', previewImages[0]);
                    // 尝试使用替代图片源
                    const img = e.currentTarget;
                    if (img.src !== 'https://via.placeholder.com/800x600?text=图片加载失败') {
                      img.src = 'https://via.placeholder.com/800x600?text=图片加载失败';
                    }
                  }}
                />
              ) : (
                // 多张图片显示 - 根据宽高比使用不同的布局
                <div className={`grid ${getGridStyle()} w-full h-full p-3`}>
                  {previewImages.slice(0, count).map((imgUrl, index) => (
                    <div
                      key={index}
                      className={`relative ${getImageContainerStyle(index)} flex items-center justify-center overflow-hidden rounded-lg shadow-md bg-dark-800 bg-opacity-30 transition-transform hover:scale-[1.02] duration-200`}
                    >
                      <img
                        src={imgUrl}
                        alt={`${prompt} - ${index + 1}`}
                        className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openPreview(imgUrl, index)}
                        onLoad={() => console.log(`图片${index + 1}加载成功:`, imgUrl)}
                        onError={(e) => {
                          console.error(`图片${index + 1}加载失败:`, imgUrl);
                          // 尝试使用替代图片源
                          const img = e.currentTarget;
                          if (img.src !== 'https://via.placeholder.com/400x300?text=图片加载失败') {
                            img.src = 'https://via.placeholder.com/400x300?text=图片加载失败';
                          }
                        }}
                      />
                      {/* 添加图片序号标识 */}
                      <div className="absolute bottom-2 right-2 bg-dark-900 bg-opacity-80 text-white text-xs px-2 py-1 rounded-full shadow-sm">
                        {index + 1}/{count}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 提示信息 */}
          {!isGenerating && !prompt && !previewImages && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-3">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-4xl mb-2"
              >
                🎨
              </motion.div>
              <h2 className="text-lg font-semibold text-white mb-2">准备好创作了吗？</h2>
              <p className="text-dark-300 text-sm max-w-md">
                在左侧选择一个AI模型，输入提示词，然后点击"生成图像"按钮开始创作。
              </p>
            </div>
          )}

          {/* 模型信息 */}
          {selectedModel && !isGenerating && prompt && !previewImages && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <div className="max-w-md">
                <h3 className="text-lg font-medium text-white mb-2">
                  {selectedModel.name}
                </h3>
                <p className="text-dark-300 text-sm mb-4">
                  {prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {selectedModel.capabilities.map((capability, index) => (
                    <span key={index} className="px-3 py-1 bg-dark-600 rounded-full text-sm text-dark-200">
                      {capability}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 提示信息 */}
        <div className="mt-1 text-center text-dark-300 text-xs">
          {isGenerating ? (
            <p>生成可能需要几秒钟时间，请耐心等待...</p>
          ) : (
            <p>图像将在这里预览，生成后可在画廊中查看</p>
          )}
        </div>
      </div>

      {/* 图片预览组件 */}
      <ImagePreview
        imageUrl={previewImageUrl}
        isOpen={previewOpen}
        onClose={closePreview}
        alt={prompt}
        images={previewImages || []}
        currentIndex={previewImageIndex}
        onNavigate={navigateToImage}
      />
    </div>
  );
};

export default Canvas;
